import { arrayBufferToBase64, base64ToArrayBuffer } from "./utils.mjs";

// ===== Quantisation & timing =====
const TAU = Math.PI * 2;

// centimetre precision
const POS_SCALE = 100; // metres -> centimetres
const TIME_SCALE_INT = 100; // centiseconds per second
const SAMPLE_RATE = 10; // Hz
const SAMPLE_INTERVAL_MS = 1000 / SAMPLE_RATE;

const HEADING_TO_U16 = 65535 / TAU;
const U16_TO_HEADING = TAU / 65535;

const GHOST_KEY = "ghostDataV3";

// ===== Runtime buffers (delta-encoded samples) =====
// Each sample: { tCS:uint24, dxI16:int16, dyI16:int16, hU16:uint16 }
let localGhostSamples = [];
let localFirstSampleMs = null;
let localLastSampleMs = -Infinity;

// keep last quantised absolute to compute deltas
let lastQx = 0; // int (cm)
let lastQy = 0; // int (cm)
let haveOrigin = false;
let startQx = 0; // header absolute origin (cm)
let startQy = 0;

function normAngle(a) {
  a %= TAU;
  if (a < 0) a += TAU;
  return a;
}

export function recordLocalGhost(tAbsMs, x, y, heading) {
  if (localFirstSampleMs === null) {
    localFirstSampleMs = tAbsMs;
    localLastSampleMs = tAbsMs;
  }

  // rate gate
  if (tAbsMs - localLastSampleMs < SAMPLE_INTERVAL_MS) return;
  localLastSampleMs = tAbsMs;

  const tSec = (tAbsMs - localFirstSampleMs) / 1000;

  // Optional: dedupe if basically unchanged
  const last = localGhostSamples[localGhostSamples.length - 1];
  if (last) {
    const dx = x - last.x;
    const dy = y - last.y;
    const d2 = dx * dx + dy * dy;
    const dHeading = Math.abs(heading - last.heading);
    if (d2 < 1e-6 && dHeading < 1e-3) {
      return;
    }
  }

  localGhostSamples.push({ t: tSec, x, y, heading });
}

export function clearLocalGhost() {
  localGhostSamples = [];
  localFirstSampleMs = null;
  localLastSampleMs = -Infinity;
  haveOrigin = false;
  startQx = startQy = lastQx = lastQy = 0;
}

export function resetLocalGhost(name = GHOST_KEY) {
  localStorage.removeItem(name);
  clearLocalGhost();
}

// Binary + sequential search helper (unchanged)
function findGhostSpan(samples, tSec, lastIdx = null) {
  const n = samples.length;
  if (!n || n === 0) return { i0: -1, i1: -1, alpha: 0 };

  if (tSec <= samples[0].t) return { i0: 0, i1: 0, alpha: 0 };
  if (tSec >= samples[n - 1].t) return { i0: n - 1, i1: n - 1, alpha: 1 };

  // Sequential scan for monotonic playback
  if (lastIdx !== null && lastIdx >= 0 && lastIdx < n) {
    let i = lastIdx;

    if (tSec >= samples[i].t) {
      while (i + 1 < n && samples[i + 1].t <= tSec) i++;
      if (samples[i].t <= tSec && tSec < samples[i + 1].t) {
        const t0 = samples[i].t,
          t1 = samples[i + 1].t;
        const alpha = (tSec - t0) / (t1 - t0);
        return { i0: i, i1: i + 1, alpha };
      }
    } else {
      while (i > 0 && samples[i - 1].t > tSec) i--;
      if (samples[i].t <= tSec && tSec < samples[i + 1].t) {
        const t0 = samples[i].t,
          t1 = samples[i + 1].t;
        const alpha = (tSec - t0) / (t1 - t0);
        return { i0: i, i1: i + 1, alpha };
      }
    }
  }

  // Binary search fallback
  let lo = 0,
    hi = n - 1;
  while (lo + 1 < hi) {
    const mid = (lo + hi) >> 1;
    if (samples[mid].t <= tSec) lo = mid;
    else hi = mid;
  }
  const t0 = samples[lo].t,
    t1 = samples[lo + 1].t;
  const alpha = (tSec - t0) / (t1 - t0);
  return { i0: lo, i1: lo + 1, alpha };
}

// Safe heading interpolation
function lerpAngle(a, b, alpha) {
  let d = b - a;
  d = ((d + Math.PI) % TAU) - Math.PI;
  return a + d * alpha;
}

export function sampleGhostAt(samples, tMs, lastIdx = null) {
  const tSec = tMs / 1000;

  const { i0, i1, alpha } = findGhostSpan(samples, tSec, lastIdx);
  if (i0 < 0) return null;

  const s0 = samples[i0],
    s1 = samples[i1];
  if (i0 === i1 || !isFinite(alpha)) {
    return { x: s0.x, y: s0.y, heading: s0.heading, index: i0 };
  }

  const x = s0.x + (s1.x - s0.x) * alpha;
  const y = s0.y + (s1.y - s0.y) * alpha;
  return { x, y, heading: s1.heading, index: i0 };
}

// Build GH3 ArrayBuffer from current in-memory samples
function packGhost(samples) {
  const n = samples.length;
  const HEADER = 21;
  const BYTES_PER_SAMPLE = 9;

  if (n === 0) {
    return { buffer: new ArrayBuffer(0), samples: 0, bytes: 0 };
  }

  const buffer = new ArrayBuffer(HEADER + n * BYTES_PER_SAMPLE);
  const view = new DataView(buffer);
  let o = 0;

  // ---- Header "GH3\0" ----
  view.setUint8(o++, 0x47); // 'G'
  view.setUint8(o++, 0x48); // 'H'
  view.setUint8(o++, 0x33); // '3'
  view.setUint8(o++, 0x00);
  view.setUint8(o++, 3); // version
  view.setUint16(o, POS_SCALE, true);
  o += 2; // e.g. 100
  view.setUint16(o, TIME_SCALE_INT, true);
  o += 2; // e.g. 100

  // Quantise first sample as absolute origin (cm)
  const first = samples[0];

  let qx = Math.round(first.x * POS_SCALE);
  let qy = Math.round(first.y * POS_SCALE);

  view.setInt32(o, qx, true);
  o += 4; // startQx in cm
  view.setInt32(o, qy, true);
  o += 4; // startQy in cm

  view.setUint32(o, n, true);
  o += 4; // sample count

  // ---- Samples ----
  let lastQx = qx;
  let lastQy = qy;

  for (let i = 0; i < n; i++) {
    const s = samples[i];

    // time: seconds -> centiseconds (uint24)
    const tCS = Math.min(
      0xffffff,
      Math.round(s.t * TIME_SCALE_INT) // TIME_SCALE_INT=100 → 100*seconds
    );

    qx = Math.round(s.x * POS_SCALE);
    qy = Math.round(s.y * POS_SCALE);

    let dxI16 = qx - lastQx;
    let dyI16 = qy - lastQy;

    // clamp to int16 range
    if (dxI16 < -32768) dxI16 = -32768;
    if (dxI16 > 32767) dxI16 = 32767;
    if (dyI16 < -32768) dyI16 = -32768;
    if (dyI16 > 32767) dyI16 = 32767;

    // heading: radians → uint16 [0..65535]
    const hNorm = ((s.heading % TAU) + TAU) % TAU;
    const hU16 = Math.round(hNorm * HEADING_TO_U16) & 0xffff;

    // write sample
    view.setInt16(o, dxI16, true);
    o += 2;
    view.setInt16(o, dyI16, true);
    o += 2;
    view.setUint16(o, hU16, true);
    o += 2;

    view.setUint8(o++, tCS & 0xff);
    view.setUint8(o++, (tCS >>> 8) & 0xff);
    view.setUint8(o++, (tCS >>> 16) & 0xff);

    lastQx += dxI16;
    lastQy += dyI16;
  }

  return { buffer, samples: n, bytes: HEADER + n * BYTES_PER_SAMPLE };
}

// Decode GH3 buffer into runtime-friendly samples (seconds/metres/radians)
export function parseGhostFromArrayBuffer(buffer) {
  if (!(buffer instanceof ArrayBuffer) || buffer.byteLength < 21) return null;

  const view = new DataView(buffer);

  // Magic check
  if (!(view.getUint8(0) === 0x47 && view.getUint8(1) === 0x48)) return null; // "GH"
  let o = 4;

  const version = view.getUint8(o++); // expect 3
  const posScale = view.getUint16(o, true);
  o += 2; // expect 100
  const timeScale = view.getUint16(o, true);
  o += 2; // expect 100
  let qx = view.getInt32(o, true);
  o += 4; // startQx (cm)
  let qy = view.getInt32(o, true);
  o += 4; // startQy (cm)
  const count = view.getUint32(o, true);
  o += 4;

  const samples = new Array(count);
  for (let i = 0; i < count; i++) {
    const dxI16 = view.getInt16(o, true);
    o += 2;
    const dyI16 = view.getInt16(o, true);
    o += 2;
    const hU16 = view.getUint16(o, true);
    o += 2;
    const tCS =
      view.getUint8(o) |
      (view.getUint8(o + 1) << 8) |
      (view.getUint8(o + 2) << 16);
    o += 3;

    qx += dxI16;
    qy += dyI16;

    samples[i] = {
      t: tCS / timeScale, // seconds since lap start
      x: qx / posScale, // metres
      y: qy / posScale,
      heading: hU16 * U16_TO_HEADING, // radians
    };
  }

  return { version, posScale, timeScale, samples };
}

// Pack current recording and store as Base64 (debug path)
export function saveLocalGhost(name = GHOST_KEY) {
  const { buffer, samples, bytes } = packGhost(localGhostSamples);
  if (samples === 0) return { samples, bytes, key: name, b64Bytes: 0 };

  const b64 = arrayBufferToBase64(buffer);
  localStorage.setItem(name, b64);
  return { samples, bytes, key: name, b64Bytes: b64.length };
}

// Load from Base64 in localStorage (debug path)
export function loadLocalGhost(name = GHOST_KEY) {
  const b64 = localStorage.getItem(name);
  if (!b64) return null;
  const buf = base64ToArrayBuffer(b64);
  return parseGhostFromArrayBuffer(buf);
}

export function getGhostArrayBuffer(samples) {
  return packGhost(samples).buffer; // may be 0-length if nothing recorded
}

export function getLocalGhostSamples() {
  return localGhostSamples;
}
